import { useCopyInvoiceReportService } from '@/context/services/invoice_reports/InvoiceReportCopy.service';
import { isInvalidError, isNotFoundError } from '@/context/services/type';
import { useCheckInvoiceReportsProtectedResources } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportsProtectedResources';
import type { DuplicateParams } from '@/features/invoice_reports/components/ConfirmDuplicateModal/context/ConfirmDuplicateModalContext';
import {
  extractInvoiceReportErrorMessage,
  getStatusCodeFromError,
} from '@/features/invoice_reports/utils/extractErrorMessage';
import { useTriggerFindTargetReport } from '@/features/invoice_reports/components/List/context/TriggerFindTargetReportContext';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback } from 'react';
import { toHtmlNode } from '@/utils/toHtmlNode';

type UseDuplicateInvoiceReportProps = {
  refetch: () => Promise<unknown>;
  onDrawerClose: () => void;
  selectedId: string | null;
};

const TOAST_DURATION = 5;

export const useDuplicateInvoiceReport = ({
  refetch,
  onDrawerClose,
  selectedId,
}: UseDuplicateInvoiceReportProps) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { mutateAsync: copyAsync } = useCopyInvoiceReportService();
  const { handleCheckPermission } = useCheckInvoiceReportsProtectedResources();
  const { onTriggerBackToPageOneIfNoData } = useTriggerFindTargetReport();

  const duplicateReport = useCallback(
    (params: DuplicateParams) => async () => {
      try {
        const { editPath } = await copyAsync(params.id);
        if (editPath) {
          window.location.href = editPath;
        }
      } catch (error) {
        const errorMessage = extractInvoiceReportErrorMessage(error, t);
        const statusCode = getStatusCodeFromError(error);

        if (isNotFoundError(statusCode)) {
          if (params.id === selectedId) {
            onDrawerClose();
          }
          notify.error({
            title: toHtmlNode(errorMessage ?? t('system_error')),
            duration: TOAST_DURATION,
          });
          await refetch();
          return;
        }

        if (isInvalidError(statusCode)) {
          await handleCheckPermission({
            fnCheckPermission: refetch,
            onCheckSuccess: () => {
              if (params.id === selectedId) {
                onDrawerClose();
              }
              notify.error({
                title: toHtmlNode(errorMessage ?? t('system_error')),
                duration: TOAST_DURATION,
              });
              onTriggerBackToPageOneIfNoData();
            },
            onCheckFailure: () => {
              location.reload();
            },
          });
          return;
        }

        await handleCheckPermission({
          fnCheckPermission: refetch,
          onCheckSuccess: () => {
            if (params.id === selectedId) {
              onDrawerClose();
            }
            notify.error({
              title: toHtmlNode(errorMessage ?? t('system_error')),
              duration: TOAST_DURATION,
            });
            onTriggerBackToPageOneIfNoData();
          },
          onCheckFailure: () => {
            location.reload();
          },
        });
      }
    },
    [
      copyAsync,
      handleCheckPermission,
      notify,
      onTriggerBackToPageOneIfNoData,
      onDrawerClose,
      refetch,
      selectedId,
      t,
    ]
  );

  return {
    duplicateReport,
  };
};
